/**
 * User-facing copy for OAuth sign-in outcomes, kept in one place so it
 * doesn't drift from what the UI Designer actually wrote (relaying Nachi's
 * policy) as the backend contract settles around it.
 *
 * These two error codes are UI copy, not backend copy: avrio-backend only
 * needs to return a distinguishable `code`, and the exact sentence a
 * hiring-team member reads is decided and owned here.
 */

import { AxiosError } from 'axios';
import { BackendErrorBody, OAuthProvider } from './auth.fsm.types';

const providerLabel = (provider?: OAuthProvider): string =>
  provider === 'microsoft' ? 'Microsoft' : 'Google';

/** The backend's JSON error body, if this error actually carries one. A
 * network failure or this app's own OAuth stub error has no `.response`. */
export const backendErrorBody = (
  error?: AxiosError,
): BackendErrorBody | undefined =>
  error?.response?.data as BackendErrorBody | undefined;

/**
 * The exact sentence to show for an OAuth sign-in failure. Falls back to
 * whatever message is otherwise available (the backend's generic
 * `message`, or the raw AxiosError message for a client-side failure like
 * this app's current stub) for any code this function doesn't recognise --
 * which today is every non-OAuth login/reset-password failure, since none
 * of those responses carry an OAuth error `code` at all.
 */
export const resolveOAuthErrorMessage = (
  error?: AxiosError,
  provider?: OAuthProvider,
): string | undefined => {
  const body = backendErrorBody(error);
  const label = providerLabel(provider);

  switch (body?.code) {
    case 'NO_ACCOUNT_MATCH':
      return 'No Avrio account found for this email — contact your workspace admin.';
    case 'EMAIL_NOT_VERIFIED':
      return `Your ${label} account's email isn't verified. Verify it with ${label}, or sign in with your Avrio password instead.`;
    default:
      return body?.message ?? error?.message;
  }
};

/** "Signed in via Google — linked to your Avrio account." */
export const justLinkedMessage = (provider?: OAuthProvider): string =>
  `Signed in via ${providerLabel(provider)} — linked to your Avrio account.`;
